import { Formik } from "formik";
import { FormattedMessage, injectIntl } from "react-intl";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import { DatePicker } from "common/components/datepicker";
import React, { Component } from "react";
import { withApp } from "common/context";
import moment from "moment";

/**
 * Date range picker utility - allows users to select from a preset list or custom dates
 *
 * <DateRangePicker onRef={el => { ... }} onSelect={({ start_date, end_date }) => { ... }}/>
 *
 * Reference Usage: picker.show();
 */
class DateRangePicker extends Component {
    constructor(props, ref) {
        super(props, ref);
        this.state = {
            show: false,
            start_date: moment().subtract(30, 'days').toDate().getTime(),
            end_date: moment().toDate().getTime(),
            selected_range: 'month',
            max_date_range: '180' // 180 days
        };
        this.ref = ref;
    }

    componentDidMount() {
        if (typeof (this.props.onRef) == 'function')
            this.props.onRef(this);

        if (this.props.range) {
            this.setState({ selected_range: this.props.range });
        }

    }

    componentWillUnmount() {
        if (typeof (this.props.onRef) == 'function')
            this.props.onRef(undefined);
    }

    show = (values = {}) => {

        var start_date = values && values.start_date ? values.start_date : moment().subtract(30, 'days').toDate().getTime();
        var end_date = values && values.end_date ? values.end_date : moment().toDate().getTime();
        var selected_range = values && values.selected_range ? values.selected_range : this.state.selected_range || 'month';

        this.setState({
            show: true,
            start_date,
            end_date,
            selected_range
        });

    };

    handleHide = () => {
        this.setState({
            show: false
        });
    };

    handleSubmit = ({ start_date, end_date, date_select }) => {
        if (typeof (this.props.onSelect) == 'function')
            this.props.onSelect({ start_date, end_date, selected_range: date_select });
        this.handleHide();
    };

    render() {

        const { selected_range, start_date, end_date } = this.state;
        const { app, intl } = this.props;
        const { locale } = app;

        let now = moment(new Date());
        let week_ago = now.clone().subtract(7, 'days');
        let month_ago = now.clone().subtract(30, 'days');
        let three_month_ago = now.clone().subtract(90, 'days');
        let six_month_ago = now.clone().subtract(180, 'days');

        const min_date = moment(end_date).subtract(1, 'year').toDate();
        const max_date = moment().toDate();

        return (
            <Modal
                dialogClassName="react-datepicker-modal"
                className="date-picker-modal"
                show={this.state.show}
                onHide={this.handleHide}
                >
                <Formik
                    initialValues={{
                        date_select: selected_range,
                        start_date,
                        end_date
                    }}
                    onSubmit={(values, actions) => {
                        this.handleSubmit(values);
                    }}
                >
                    {({ values, touched, errors, handleChange, handleSubmit, setFieldValue, setFieldTouched }) => (
                        <Form onSubmit={handleSubmit}>
                            <Modal.Body className="pt-0 px-0">
                                <div className="range-selector-header">
                                    <Form.Select
                                        name="date_select"
                                        label="date_select"
                                        className="date-range-selector"
                                        value={values.date_select}
                                        isValid={touched.date_select && !errors.date_select}
                                        isInvalid={touched.date_select && errors.date_select}
                                        disabled={this.state.saving}
                                        onChange={e => {

                                            //set the custom date picker range so it matches the select value set by the user
                                            if (e.target.value === "six_month") {
                                                setFieldValue('start_date', moment(six_month_ago).toDate().getTime());
                                                setFieldValue('end_date', moment(now).toDate().getTime());
                                                this.setState({
                                                    start_date: moment(six_month_ago).toDate().getTime(),
                                                    end_date: moment(now).toDate().getTime()
                                                });
                                            }
                                            if (e.target.value === "three_month") {
                                                setFieldValue('start_date', moment(three_month_ago).toDate().getTime());
                                                setFieldValue('end_date', moment(now).toDate().getTime());
                                                this.setState({
                                                    start_date: moment(three_month_ago).toDate().getTime(),
                                                    end_date: moment(now).toDate().getTime()
                                                });
                                            }
                                            if (e.target.value === "month") {
                                                setFieldValue('start_date', moment(month_ago).toDate().getTime());
                                                setFieldValue('end_date', moment(now).toDate().getTime());
                                                this.setState({
                                                    start_date: moment(month_ago).toDate().getTime(),
                                                    end_date: moment(now).toDate().getTime()
                                                });
                                            }
                                            if (e.target.value === "week") {
                                                setFieldValue('start_date', moment(week_ago).toDate().getTime());
                                                setFieldValue('end_date', moment(now).toDate().getTime());
                                                this.setState({
                                                    start_date: moment(week_ago).toDate().getTime(),
                                                    end_date: moment(now).toDate().getTime()
                                                });
                                            }
                                            handleChange(e);
                                            this.setState({ selected_range: e.target.value });
                                        }}
                                    >
                                        <option value="week">{intl.formatMessage({ id: "label.past_7_days", defaultMessage: 'Past 7 days' })}</option>
                                        <option value="month">{intl.formatMessage({ id: "label.past_30_days", defaultMessage: 'Past 30 days' })}</option>
                                        <option value="three_month">{intl.formatMessage({ id: "label.past_90_days", defaultMessage: 'Past 90 days' })}</option>
                                        <option value="six_month">{intl.formatMessage({ id: "label.past_180_days", defaultMessage: 'Past 180 days' })}</option>
                                        <option value="custom">{intl.formatMessage({ id: "label.custom", defaultMessage: "Custom" })}</option>
                                    </Form.Select>
                                </div>
                                <Row className="p-2 g-0">
                                    <Col lg={6}>
                                        <div className="text-center p-relative">
                                            <div className="date-picker-header-container">
                                                <h6><FormattedMessage defaultMessage="Start date" id="label.start_date" /></h6>
                                            </div>
                                            <DatePicker
                                                className="google-meet-datepicker"
                                                showPopperArrow={false}
                                                inline
                                                selected={values.start_date}
                                                minDate={min_date}
                                                maxDate={max_date}
                                                onChange={date => {
                                                    setFieldValue('start_date', date.getTime());
                                                    setFieldTouched('start_date');
                                                    setFieldValue('date_select', 'custom');
                                                    this.setState({ selected_range: 'custom' });
                                                }}
                                                dateFormat="MMM d, yyyy"
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={6}>
                                        <div className="text-center">
                                            <div className="date-picker-header-container">
                                                <h6><FormattedMessage defaultMessage="End date" id="label.end_date" /></h6>
                                            </div>
                                            <DatePicker
                                                className="google-meet-datepicker"
                                                showPopperArrow={false}
                                                inline
                                                selected={values.end_date}
                                                minDate={min_date}
                                                maxDate={max_date}
                                                onChange={date => {
                                                    setFieldValue('end_date', date.getTime());
                                                    setFieldTouched('end_date');
                                                    setFieldValue('date_select', 'custom');
                                                    this.setState({ selected_range: 'custom' });
                                                }}
                                                dateFormat="MMM d, yyyy"
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <div className="px-2 d-flex justify-content-end">
                                    <Button className="me-1" variant="secondary" onClick={this.handleHide}>
                                        <FormattedMessage defaultMessage="Cancel" id="label.cancel" />
                                    </Button>

                                    <Button variant="primary" onClick={handleSubmit}>
                                        <FormattedMessage defaultMessage="Apply" id="label.apply" />
                                    </Button>
                                </div>
                            </Modal.Body>
                        </Form>
                    )}
                </Formik>
            </Modal>
        );
    }
}

export default injectIntl(withApp(DateRangePicker));
